import React, { useEffect } from "react";
import * as Yup from "yup";
import {
  WSActions,
  WSButton,
  WSCard,
  WSForm,
  WSGrid,
  WSControl,
  WSInfoBox,
  WSInputDate,
  WSInputMask,
  WSInputText,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useFlowContext } from "../../../../shared/components/Flow";
import { validatorName } from "../../../../shared/validators/validatorName";
import { IMeridioEnrollmentDependent } from "../../../../services/api/benefits/meridioPlanEnrollment/types";
import { CoverageTier, EnrollMeridioFlowState } from "./types";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

const DEPENDENT_TIERS: CoverageTier[] = ["IS", "IC", "IF"];

type DependentFormData = {
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  dob: Date | null;
  ssn: string;
  gender: string;
  relationship: string;
};

type FormData = {
  dependents: DependentFormData[];
};

const emptyDependent: DependentFormData = {
  firstName: "",
  middleName: "",
  lastName: "",
  suffix: "",
  dob: null,
  ssn: "",
  gender: "",
  relationship: ""
};

const dependentItemSchema = Yup.object().shape({
  firstName: validatorName.required("Required"),
  lastName: validatorName.required("Required"),
  dob: Yup.date().nullable().required("Required"),
  ssn: Yup.string().required("Required"),
  gender: Yup.string().required("Required"),
  relationship: Yup.string().required("Required")
});

const buildValidationSchema = (
  requiresSpouse: boolean,
  requiresChild: boolean
) =>
  Yup.object().shape({
    dependents: Yup.array()
      .of(dependentItemSchema)
      .test("required-dependents", "", function (dependents) {
        const list = dependents ?? [];
        const missing: string[] = [];
        if (requiresSpouse && !list.some(d => d?.relationship === "Spouse")) {
          missing.push("a spouse");
        }
        if (requiresChild && !list.some(d => d?.relationship === "Child")) {
          missing.push("at least one child");
        }
        if (missing.length > 0) {
          return this.createError({
            message: `Your selected coverage requires ${missing.join(" and ")}.`
          });
        }
        return true;
      })
  });

const SPOUSE_TIERS: CoverageTier[] = ["IS", "IF"];

const DependentFields: React.FC<{ spouseAllowed: boolean }> = ({
  spouseAllowed
}) => {
  const { control, watch, errors } = useFormContext<FormData>();
  const { fields, append, remove } = useFieldArray<FormData>({
    control,
    name: "dependents"
  });
  const currentDependents: DependentFormData[] = watch("dependents") ?? [];

  const spouseAlreadyAdded = currentDependents.some(
    d => d.relationship === "Spouse"
  );

  return (
    <>
      {fields.map((field, index) => {
        const isThisRowSpouse =
          currentDependents[index]?.relationship === "Spouse";
        const showSpouseOption =
          spouseAllowed && (!spouseAlreadyAdded || isThisRowSpouse);

        const relationshipOptions = [
          ...(showSpouseOption ? [{ label: "Spouse", value: "Spouse" }] : []),
          { label: "Child", value: "Child" }
        ];

        return (
          <WSCard key={field.id} mb="L">
            <WSText weight="medium" mb="XL">
              Dependent {index + 1}
            </WSText>
            <WSList gap="XL">
              <WSGrid gutter="L">
                <WSGrid.Item span={{ xs: "12", s: "4" }}>
                  <WSForm.Field
                    name={`dependents[${index}].firstName`}
                    label="Legal first name *"
                    component={WSInputText}
                  />
                </WSGrid.Item>
                <WSGrid.Item span={{ xs: "12", s: "4" }}>
                  <WSForm.Field
                    name={`dependents[${index}].middleName`}
                    label="Middle name"
                    component={WSInputText}
                  />
                </WSGrid.Item>
                <WSGrid.Item span={{ xs: "12", s: "4" }}>
                  <WSForm.Field
                    name={`dependents[${index}].lastName`}
                    label="Legal last name *"
                    component={WSInputText}
                  />
                </WSGrid.Item>
              </WSGrid>
              <WSForm.Field
                name={`dependents[${index}].suffix`}
                label="Suffix (optional)"
                component={WSInputText}
              />
              <WSForm.Field
                name={`dependents[${index}].dob`}
                label="Date of birth *"
                component={WSInputDate}
              />
              <WSForm.Field
                name={`dependents[${index}].ssn`}
                label="Social Security Number (SSN) *"
                component={WSInputMask}
                componentProps={{ mask: "999-99-9999", unmask: true }}
              />
              <WSForm.Field
                name={`dependents[${index}].gender`}
                label="Sex *"
                component={WSSelect}
                componentProps={{
                  options: [
                    { label: "Male", value: "Male" },
                    { label: "Female", value: "Female" }
                  ]
                }}
              />
              <WSForm.Field
                name={`dependents[${index}].relationship`}
                label="Relationship to you *"
                component={WSSelect}
                componentProps={{ options: relationshipOptions }}
              />
            </WSList>
            {fields.length > 1 && (
              <WSButton.Link
                mt="XL"
                destructive
                type="button"
                onClick={() => remove(index)}
              >
                Remove dependent
              </WSButton.Link>
            )}
          </WSCard>
        );
      })}
      {(errors.dependents as any)?.message && (
        <WSText.ParagraphSm color="red400">
          {(errors.dependents as any).message}
        </WSText.ParagraphSm>
      )}
      <WSCard>
        <WSButton.Link
          icon="plus"
          type="button"
          onClick={() => append(emptyDependent as any)}
        >
          Add dependent
        </WSButton.Link>
      </WSCard>
    </>
  );
};

export const StepAddDependents: React.FC<FlowStep> = props => {
  const [flowState, setFlowState] = useFlowContext<EnrollMeridioFlowState>();

  const planTiers = Object.values(flowState.planSelections ?? {});

  const requiresDependents = planTiers.some(tier =>
    DEPENDENT_TIERS.includes(tier)
  );

  const spouseAllowed = planTiers.some(tier => SPOUSE_TIERS.includes(tier));
  const requiresChild = planTiers.some(tier =>
    (["IC", "IF"] as CoverageTier[]).includes(tier)
  );

  useEffect(() => {
    if (!requiresDependents) {
      props.onNext?.();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (values: FormData) => {
    setFlowState(prev => ({
      ...prev,
      dependents: values.dependents.map(dep => ({
        ...dep,
        dob: dep.dob instanceof Date ? dep.dob.toISOString() : dep.dob
      })) as unknown as IMeridioEnrollmentDependent[]
    }));
    props.onNext?.();
  };

  if (!requiresDependents) {
    return null;
  }

  const defaultDependents: DependentFormData[] =
    flowState.dependents && flowState.dependents.length > 0
      ? flowState.dependents.map(dep => ({
          ...dep,
          dob: dep.dob ? new Date(dep.dob as unknown as string) : null
        }))
      : [emptyDependent];

  return (
    <WSForm<FormData>
      defaultValues={{
        dependents: defaultDependents
      }}
      onSubmit={handleSubmit}
      validationSchema={buildValidationSchema(spouseAllowed, requiresChild)}
    >
      <>
        <WSText.Heading3 weight="medium" mb="S">
          Add dependents
        </WSText.Heading3>
        <WSText.ParagraphSm weight="book" color="gray500" mb="XL">
          Provide information about each person who will be covered by your
          plan.
        </WSText.ParagraphSm>

        <WSList gap="XL">
          <WSCard>
            <WSInfoBox transparent icon="help-circle">
              {"{Standard coverage and validation language by Meridio}"}
              <WSText.ParagraphSm mt="XS">
                <b>Insurer:</b> {"{Meridio info}"}
              </WSText.ParagraphSm>
            </WSInfoBox>
          </WSCard>

          <DependentFields spouseAllowed={spouseAllowed} />

          {requiresChild && (
            <WSCard>
              <WSInfoBox>
                <WSText.ParagraphSm weight="book" color="gray700" mb="L">
                  Your child's information will be shared with Meridio to
                  process their insurance enrollment. You may revoke consent
                  anytime at wingspan support.
                </WSText.ParagraphSm>
                <WSControl
                  type="checkbox"
                  value={flowState.childMeridioSharingConsent === true}
                  onChange={checked =>
                    setFlowState(prev => ({
                      ...prev,
                      childMeridioSharingConsent: checked
                    }))
                  }
                  label="I consent to sharing my child's information with Meridio for enrollment processing."
                  size="S"
                />
              </WSInfoBox>
            </WSCard>
          )}
        </WSList>

        <WSActions
          mt="XL"
          alignment="fill"
          buttons={[
            {
              kind: "Tertiary",
              label: "Back",
              onClick: props.onBack,
              type: "button"
            },
            {
              label: "Continue",
              disabled: requiresChild && !flowState.childMeridioSharingConsent
            }
          ]}
        />
      </>
    </WSForm>
  );
};
